/**
 * StartOnboardingPage - the dedicated "/start" entry point.
 *
 * Opening this URL ALWAYS begins a fresh anonymous onboarding session (any
 * existing auth/session is discarded), then hands off to the wizard at the
 * path-choice step. Useful for marketing links and "try it now" buttons.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button, Stack } from '@mui/material';
import { startAnonymousSession } from './anonymousSession';

export default function StartOnboardingPage() {
  const navigate = useNavigate();
  const startedRef = useRef(false);
  const [error, setError] = useState<string | null>(null);

  const run = async () => {
    setError(null);
    try {
      await startAnonymousSession({ fresh: true });
      navigate('/onboarding', { replace: true });
    } catch (e: any) {
      setError(e instanceof Error ? e.message : 'Could not start a session. Please try again.');
    }
  };

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;
    void run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 3 }}>
      {error ? (
        <Stack spacing={2} alignItems="center" sx={{ maxWidth: 380, textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>Something went wrong</Typography>
          <Typography variant="body2" color="text.secondary">{error}</Typography>
          <Stack direction="row" spacing={1.5}>
            <Button variant="contained" onClick={() => { startedRef.current = true; void run(); }}>
              Try again
            </Button>
            <Button variant="text" onClick={() => navigate('/login', { replace: true })}>
              Sign in instead
            </Button>
          </Stack>
        </Stack>
      ) : (
        <CircularProgress />
      )}
    </Box>
  );
}
